import { ReactNode } from 'react';
import { StatusBadge, StatusType } from './StatusBadge';
import { ConfidenceBar } from './ConfidenceBar';

/**
 * OpportunityCard - Insight card with status, confidence bar, reasons, recommendation
 *
 * Pattern from previous version screenshots:
 * - Header: Icon + Title + Status Badge
 * - Confidence bar with percentage
 * - "RAZONES" section with bullet list
 * - "RECOMENDACIÓN" section with action text
 * - Optional CTA button
 */

interface OpportunityCardProps {
  /** Icon element or emoji */
  icon?: ReactNode;
  /** Card title */
  title: string;
  /** Status indicator */
  status: StatusType;
  /** Confidence percentage (0-100) */
  confidence: number;
  /** List of reasons/supporting points */
  reasons: string[];
  /** Recommendation text */
  recommendation: string;
  /** Optional potential value (e.g., "+$3.1M annual") */
  potential?: string;
  /** CTA button text */
  ctaText?: string;
  /** CTA click handler */
  onCtaClick?: () => void;
  /** CTA href (alternative to onClick) */
  ctaHref?: string;
  /** Language for labels */
  language?: 'en' | 'es';
  /** Additional CSS classes */
  className?: string;
}

export function OpportunityCard({
  icon,
  title,
  status,
  confidence,
  reasons,
  recommendation,
  potential,
  ctaText,
  onCtaClick,
  ctaHref,
  language = 'en',
  className = '',
}: OpportunityCardProps) {
  const t = language === 'es';

  const statusIcons: Record<StatusType, string> = {
    approved: '✓',
    review: '⚠',
    risk: '✕',
    pending: '○',
    'coming-soon': '◐',
  };

  const displayIcon = icon || statusIcons[status];

  const hasCta = ctaText && (onCtaClick || ctaHref);

  const renderCta = () => {
    if (!hasCta) return null;

    const ctaClasses = `
      mt-4 w-full py-2 rounded-lg text-sm font-medium text-center
      bg-surface-700 hover:bg-surface-600 text-text-primary
      transition-colors
    `;

    if (ctaHref) {
      return (
        <a href={ctaHref} className={ctaClasses}>
          {ctaText} →
        </a>
      );
    }

    return (
      <button onClick={onCtaClick} className={ctaClasses}>
        {ctaText} →
      </button>
    );
  };

  return (
    <div className={`card p-4 flex flex-col ${className}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          {displayIcon && (
            <span className={`text-lg ${status === 'approved' ? 'text-success-400' : status === 'review' ? 'text-warning-400' : 'text-danger-400'}`}>
              {displayIcon}
            </span>
          )}
          <h3 className="font-semibold text-white">{title}</h3>
        </div>
        <StatusBadge status={status} language={language} />
      </div>

      {/* Confidence Bar */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-text-tertiary uppercase tracking-wide">
            {t ? 'CONFIANZA' : 'CONFIDENCE'}
          </span>
        </div>
        <ConfidenceBar value={confidence} size="md" />
      </div>

      {/* Potential Value (if provided) */}
      {potential && (
        <div className="mb-3">
          <span className="text-xs text-text-tertiary uppercase tracking-wide">
            {t ? 'POTENCIAL' : 'POTENTIAL'}
          </span>
          <p className="text-primary-400 font-semibold">{potential}</p>
        </div>
      )}

      {/* Reasons */}
      <div className="mb-3 flex-1">
        <span className="text-xs text-text-tertiary uppercase tracking-wide">
          {t ? 'RAZONES' : 'REASONS'}
        </span>
        <ul className="mt-1 space-y-1">
          {reasons.map((reason, index) => (
            <li key={index} className="text-sm text-text-secondary flex items-start gap-2">
              <span className="text-text-tertiary mt-1">•</span>
              <span>{reason}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Recommendation */}
      <div className={hasCta ? '' : 'mt-auto'}>
        <span className="text-xs text-text-tertiary uppercase tracking-wide">
          {t ? 'RECOMENDACIÓN' : 'RECOMMENDATION'}
        </span>
        <p className="text-sm text-white mt-1">{recommendation}</p>
      </div>

      {/* CTA Button */}
      {renderCta()}
    </div>
  );
}

/**
 * OpportunityCardCompact - Smaller version without reasons list
 */
interface OpportunityCardCompactProps {
  icon?: ReactNode;
  title: string;
  status: StatusType;
  confidence: number;
  description: string;
  potential?: string;
  onClick?: () => void;
  language?: 'en' | 'es';
  className?: string;
}

export function OpportunityCardCompact({
  icon,
  title,
  status,
  confidence,
  description,
  potential,
  onClick,
  language = 'en',
  className = '',
}: OpportunityCardCompactProps) {
  return (
    <div
      className={`card p-3 cursor-pointer hover:border-primary-500/40 transition-all ${className}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          {icon && <span className="text-sm">{icon}</span>}
          <span className="font-medium text-white text-sm">{title}</span>
        </div>
        <StatusBadge status={status} size="sm" language={language} />
      </div>

      <ConfidenceBar value={confidence} size="sm" className="mb-2" />

      <p className="text-xs text-text-secondary line-clamp-2">{description}</p>

      {potential && (
        <p className="text-xs text-primary-400 font-medium mt-1">{potential}</p>
      )}
    </div>
  );
}

/**
 * OpportunityCardGrid - Grid layout for opportunity cards
 */
interface OpportunityCardGridProps {
  cards: OpportunityCardProps[];
  columns?: 2 | 3 | 4;
  language?: 'en' | 'es';
}

export function OpportunityCardGrid({
  cards,
  columns = 3,
  language = 'en',
}: OpportunityCardGridProps) {
  const gridCols = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-4',
  };

  return (
    <div className={`grid grid-cols-1 ${gridCols[columns]} gap-4`}>
      {cards.map((card, index) => (
        <OpportunityCard key={index} {...card} language={language} />
      ))}
    </div>
  );
}
